import { z } from "zod";
import { pendingLegalDocumentSchema } from "./legal";

export const authFormSchema = z.object({
  email: z
    .email("Correo electrónico no válido")
    .min(1, "El correo es obligatorio"),
  password: z.string().min(8, "La contraseña debe tener al menos 8 caracteres"),
  rememberMe: z.boolean().optional().default(false),
  gymSlug: z.string().optional(),
});

export type AuthFormInput = z.input<typeof authFormSchema>;
/** Alias for API request body (login) */
export type AuthForm = z.infer<typeof authFormSchema>;

export const loginDataSchema = z.object({
  token: z.string(),
  mustChangePassword: z.boolean().optional().default(false),
  mustAcceptTerms: z.boolean().optional().default(false),
});
export type LoginData = z.infer<typeof loginDataSchema>;

export const onboardingStatusSchema = z.object({
  mustChangePassword: z.boolean(),
  mustAcceptTerms: z.boolean(),
  pendingLegalDocuments: z.array(pendingLegalDocumentSchema),
});
export type OnboardingStatus = z.infer<typeof onboardingStatusSchema>;

export const forgotPasswordSchema = z.object({
  email: z.email("Correo no válido").min(1, "El correo es obligatorio"),
  gymSlug: z.string().optional(),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export interface AvailableGym {
  slug: string;
  name: string;
}

/**
 * Solo le pasa a un entrenador: su cuenta nace de un registro publico y no sirve
 * hasta que prueba el buzon. Va como clase propia y no como mensaje suelto para
 * que el login pueda ofrecerle reenviar el enlace en vez de un toast rojo.
 */
export class EmailNotVerifiedError extends Error {
  constructor(
    message = "Todavía no verificaste tu correo. Revisá tu bandeja o pedí un enlace nuevo.",
  ) {
    super(message);
    this.name = "EmailNotVerifiedError";
  }
}

export class GymSelectionRequiredError extends Error {
  readonly availableGyms: AvailableGym[];
  constructor(availableGyms: AvailableGym[], message?: string) {
    super(message ?? "Selecciona el gym al que querés ingresar");
    this.name = "GymSelectionRequiredError";
    this.availableGyms = availableGyms;
  }
}

export const resetPasswordSchema = z
  .object({
    newPassword: z.string().min(8, "Mínimo 8 caracteres"),
    confirmPassword: z.string().min(1, "Confirma la contraseña"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
